import React, { useEffect, useState } from "react";

import browsingMentalModel from "../../../assets/images/projects/maudi/browsing-mental-model.png";
import iceberg from "../../../assets/images/projects/maudi/iceberg.png";
import userConsiderations from "../../../assets/images/projects/maudi/user-considerations.png";

import { ProjectDetailProps } from "../props";
import {
  BriefBoard,
  BriefColumn,
  BriefFootnote,
  BriefGrid,
  BriefHeader,
  BriefInner,
  BriefItem,
  BriefItems,
  ComparisonRoute,
  ExpansionAudience,
  ExpansionBridge,
  ExpansionHeader,
  ExpansionInner,
  ExpansionPath,
  ExpansionSection,
  ExpansionStep,
  ExpansionTab,
  ExpansionTabs,
  Hero,
  HeroCopy,
  HeroInner,
  IcebergHotspot,
  IcebergInstruction,
  IcebergStage,
  IcebergTooltip,
  Impact,
  ImpactCopy,
  ImpactLabel,
  LayerBody,
  LayerCopy,
  LayerExplorerLayout,
  LayerIndex,
  Layers,
  LayersHeader,
  LayersInner,
  LayerTab,
  LayerTabs,
  Lightbox,
  LightboxClose,
  NeedGrid,
  OutcomeBoard,
  OutcomeHeader,
  OutcomeIntro,
  OutcomePanel,
  OutcomeTab,
  OutcomeTabs,
  PicHeader,
  PicInner,
  PicSystem,
  PrioritySheet,
  ProcessDetail,
  ProcessMap,
  ProcessNode,
  Question,
  ResearchImageButton,
  ResearchPanel,
  ResearchShell,
  ResearchTab,
  ResearchTabs,
  ResearchText,
  StoryFlow,
  StoryStep,
  WorkshopCell,
  WorkshopVisual,
} from "./maudi-style";

type LayerKey = "surface" | "skeleton" | "structure" | "scope" | "strategy";
type ResearchKey = "journey" | "considerations";
type OutcomeKey = "presentation" | "workshop" | "prioritization";
type AudienceKey = "sea" | "global";

const audiences: Record<AudienceKey, { title: string; copy: string }> = {
  sea: {
    title: "Support users in established Southeast Asian markets",
    copy: "Traveloka already had active locales and stronger product familiarity across its Southeast Asian markets. Mobile web still needed to help new and returning visitors complete their main task with only the most useful features and information.",
  },
  global: {
    title: "Earn trust from users who were new to Traveloka",
    copy: "For broader global expansion, we could not assume that people understood the brand, product conventions, or available support. They could arrive from a search engine or advertisement without the app or a previous purchase, making mobile web responsible for clarity and confidence from the first visit.",
  },
};

const layers: Record<LayerKey, { title: string; label: string; copy: string }> =
  {
    surface: {
      title: "The interface was only the visible part",
      label: "Surface",
      copy: "The first request looked like a mobile web improvement. The audit showed that changing screens alone would not answer why unfamiliar users hesitated, compared options, or moved between platforms.",
    },
    skeleton: {
      title: "The journey needed clearer support",
      label: "Skeleton",
      copy: "We reviewed the journey from first visit to transaction. This helped us see where navigation, information, and confidence broke down before the booking step.",
    },
    structure: {
      title: "User decisions happened across channels",
      label: "Structure",
      copy: "Travel planning was not limited to one Traveloka session. Users compared providers, created a shortlist, and returned when they felt ready. The product needed to support this behavior instead of assuming a direct path to purchase.",
    },
    scope: {
      title: "The recommendations had to become buildable",
      label: "Scope",
      copy: "The audit produced more opportunities than one delivery cycle could support. We grouped the recommendations, protected the core journey, and moved other ideas into planned follow-up work.",
    },
    strategy: {
      title: "The real question was trust",
      label: "Strategy",
      copy: "At the deepest layer, we were asking how mobile web could become a scalable first touchpoint for both established Southeast Asian markets and unfamiliar global users, while building enough confidence to continue.",
    },
  };

const layerOrder: LayerKey[] = [
  "surface",
  "skeleton",
  "structure",
  "scope",
  "strategy",
];

const picSteps = [
  {
    title: "Frame the brief",
    short: "Align the problem before the work starts",
    artifact: "Shared design brief and open questions",
    value: "The team began with the same problem and boundaries",
    detail:
      "Since this is a broad project, it's not only I collected the background, assumptions, supporting signals, unanswered questions, and constraints in one working space, together with Husein and Pru, we reframed the initiative into a user-centered design brief and made alignments before the design research began.",
  },
  {
    title: "Connect the right people",
    short: "Bring context to the designer at the right time",
    artifact: "Working sessions, simplified notes, and follow-ups",
    value: "Marketing, product, and design worked from shared context",
    detail:
      "The project crossed design, product, marketing, SEO, research, and engineering. I navigated and decided who needed to join each conversation, facilitated the sessions, translated unfamiliar terms into simpler notes, and followed up on missing information so we as Design team could keep moving.",
  },
  {
    title: "Support the research",
    short: "Remove blockers while the work is moving",
    artifact: "Research support and weekly asynchronous updates",
    value:
      "Questions were resolved while the work was still in progress and made it accountable as possible",
    detail:
      "I stayed close to the design research instead of waiting for a final report. I joined the planning, became a notetaker during interview sessions, and provided weekly asynchronous updates. This kept stakeholders informed and made it easier to solve new questions early.",
  },
  {
    title: "Connect the story",
    short: "Help people see beyond surface fixes",
    artifact: "A presentation structured from why, to how, to what",
    value: "The recommendations were understood in the mental-model context",
    detail:
      "The findings could easily become a long list of mobile web issues. I worked with Pru to structure the story from why, to how, to what. This helped stakeholders understand the user mental model before seeing the interface recommendations.",
  },
  {
    title: "Maintain the aftercare",
    short: "Keep the decisions alive after the presentation",
    artifact: "Scope record, next-project suggestions, and follow-up context",
    value: "The next designer could continue without restarting discovery",
    detail:
      "The audit did not end at the presentation. I and Husein maintained a high-level view of what we would address now, what needed another project, and what required monitoring. When the follow-up design work began, I continued as PIC for the upcoming project so the next designer could use the audit context from day one.",
  },
];

const icebergPoints = [
  {
    title: "Surface",
    copy: "Visual and navigation decisions were the visible part of the work.",
    x: "61%",
    y: "19%",
  },
  {
    title: "Skeleton",
    copy: "The journey and interface behavior shaped how users moved through the product.",
    x: "30%",
    y: "38%",
  },
  {
    title: "Structure",
    copy: "Information and interaction had to support a non-linear comparison journey.",
    x: "72%",
    y: "51%",
  },
  {
    title: "Scope",
    copy: "Recommendations had to fit ownership, capability, and delivery capacity.",
    x: "31%",
    y: "66%",
  },
  {
    title: "Strategy",
    copy: "At the base, the project was about user needs, trust, and the role of mobile web.",
    x: "65%",
    y: "81%",
  },
];

const IcebergVisual = () => {
  const [active, setActive] = useState(0);

  return (
    <IcebergStage aria-label="Mobile web audit iceberg with interactive layers">
      <img
        src={iceberg}
        alt="Iceberg used to explain the layers of the mobile web audit"
      />
      <IcebergInstruction>Explore each layer</IcebergInstruction>
      {icebergPoints.map((point, index) => (
        <IcebergHotspot
          key={point.title}
          type="button"
          x={point.x}
          y={point.y}
          aria-label={`Explore ${point.title}`}
          aria-pressed={active === index}
          onMouseEnter={() => setActive(index)}
          onFocus={() => setActive(index)}
          onClick={() => setActive(index)}
        >
          {index + 1}
        </IcebergHotspot>
      ))}
      <IcebergTooltip aria-live="polite">
        <strong>{icebergPoints[active].title}</strong>
        <span>{icebergPoints[active].copy}</span>
      </IcebergTooltip>
    </IcebergStage>
  );
};

const ExpansionContext = () => {
  const [active, setActive] = useState<AudienceKey>("global");

  return (
    <ExpansionSection>
      <ExpansionInner>
        <ExpansionHeader>
          <h3>The expansion changed what mobile web had to do.</h3>
          <p>
            Mobile web was becoming an acquisition surface, a first impression,
            and a trust-building experience across markets.
          </p>
        </ExpansionHeader>
        <ExpansionPath aria-label="Global acquisition path">
          <ExpansionStep>
            <span>Acquisition</span>
            <strong>Search, advertising, or a shared link</strong>
          </ExpansionStep>
          <ExpansionStep>
            <span>First touchpoint</span>
            <strong>Mobile web before an app installation</strong>
          </ExpansionStep>
          <ExpansionStep>
            <span>Design responsibility</span>
            <strong>Explain, reassure, and help users continue</strong>
          </ExpansionStep>
        </ExpansionPath>
        <ExpansionBridge>
          <ExpansionTabs aria-label="Target audience context">
            <ExpansionTab
              type="button"
              aria-pressed={active === "sea"}
              onClick={() => setActive("sea")}
            >
              Established SEA markets
            </ExpansionTab>
            <ExpansionTab
              type="button"
              aria-pressed={active === "global"}
              onClick={() => setActive("global")}
            >
              Broader global users
            </ExpansionTab>
          </ExpansionTabs>
          <ExpansionAudience aria-live="polite">
            <h4>{audiences[active].title}</h4>
            <p>{audiences[active].copy}</p>
          </ExpansionAudience>
        </ExpansionBridge>
      </ExpansionInner>
    </ExpansionSection>
  );
};

const LayerExplorer = () => {
  const [active, setActive] = useState<LayerKey>("surface");
  const layer = layers[active];
  const index = layerOrder.indexOf(active) + 1;

  return (
    <Layers>
      <LayersInner>
        <LayersHeader>
          <h3>We moved from the screen to the real problem.</h3>
          <p>Choose a layer to follow the audit.</p>
        </LayersHeader>
        <LayerExplorerLayout>
          <LayerTabs aria-label="Audit layers">
            {layerOrder.map((key, itemIndex) => (
              <LayerTab
                key={key}
                type="button"
                aria-pressed={active === key}
                onMouseEnter={() => setActive(key)}
                onFocus={() => setActive(key)}
                onClick={() => setActive(key)}
              >
                <span>0{itemIndex + 1}</span>
                {layers[key].label}
              </LayerTab>
            ))}
          </LayerTabs>
          <LayerBody aria-live="polite">
            <LayerIndex>0{index}</LayerIndex>
            <LayerCopy>
              <span>{layer.label}</span>
              <h4>{layer.title}</h4>
              <p>{layer.copy}</p>
            </LayerCopy>
          </LayerBody>
        </LayerExplorerLayout>
      </LayersInner>
    </Layers>
  );
};

const PicMaintainer = () => {
  const [active, setActive] = useState(0);

  return (
    <PicSystem>
      <PicInner>
        <PicHeader>
          <h3>My role was to maintain the system around the work.</h3>
          <p>
            Being Design PIC meant making sure the designer, stakeholders, and
            next phase always had enough context to make a decision.
          </p>
        </PicHeader>
        <ProcessMap aria-label="Design PIC operating flow">
          {picSteps.map((step, index) => (
            <ProcessNode
              key={step.title}
              type="button"
              area={
                ["frame", "connect", "research", "story", "aftercare"][index]
              }
              aria-pressed={active === index}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onClick={() => setActive(index)}
            >
              <span>0{index + 1}</span>
              <strong>{step.title}</strong>
              <small>{step.short}</small>
            </ProcessNode>
          ))}
        </ProcessMap>
        <ProcessDetail aria-live="polite">
          <h4>{picSteps[active].title}</h4>
          <p>{picSteps[active].detail}</p>
          <dl>
            <div>
              <dt>What I maintained</dt>
              <dd>{picSteps[active].artifact}</dd>
            </div>
            <div>
              <dt>Why it mattered</dt>
              <dd>{picSteps[active].value}</dd>
            </div>
          </dl>
        </ProcessDetail>
      </PicInner>
    </PicSystem>
  );
};

const BriefFramework = () => (
  <BriefBoard>
    <BriefInner>
      <BriefHeader>
        <h3>I turned the request into a brief the team could work with.</h3>
        <p>
          I used one working board to frame the problem, surface missing
          context, and turn open questions into a clear to-do list.
        </p>
      </BriefHeader>
      <BriefGrid>
        <BriefColumn tone="green">
          <h4>Questions to resolve</h4>
          <BriefItems>
            <BriefItem>
              <strong>User</strong>Who arrives through mobile web, and what do
              they already know?
            </BriefItem>
            <BriefItem>
              <strong>Behavior</strong>Where do users compare, hesitate, or
              change platforms?
            </BriefItem>
            <BriefItem>
              <strong>Evidence</strong>Which assumptions are supported, and
              which still need research?
            </BriefItem>
          </BriefItems>
        </BriefColumn>
        <BriefColumn tone="blue">
          <h4>What the team needed to do</h4>
          <BriefItems>
            <BriefItem>
              <strong>Understand</strong>Map the current journey and mobile web
              capability.
            </BriefItem>
            <BriefItem>
              <strong>Investigate</strong>Plan the audit, interviews, and
              cross-functional input.
            </BriefItem>
            <BriefItem>
              <strong>Recommend</strong>Translate findings into principles and
              feasible next steps.
            </BriefItem>
          </BriefItems>
        </BriefColumn>
        <BriefColumn tone="purple">
          <h4>Decisions needed from partners</h4>
          <BriefItems>
            <BriefItem>
              <strong>Problem</strong>Are we solving the right acquisition and
              trust problem?
            </BriefItem>
            <BriefItem>
              <strong>Success</strong>What change would be meaningful for users
              and the business?
            </BriefItem>
            <BriefItem>
              <strong>Boundary</strong>What belongs in the audit, delivery, or
              another project?
            </BriefItem>
          </BriefItems>
        </BriefColumn>
      </BriefGrid>
      <BriefFootnote>
        A simplified view of the working brief and to-do list.
      </BriefFootnote>
    </BriefInner>
  </BriefBoard>
);

const ResearchEvidence = () => {
  const [active, setActive] = useState<ResearchKey>("journey");
  const [open, setOpen] = useState(false);
  const activeImage =
    active === "journey" ? browsingMentalModel : userConsiderations;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <ResearchShell>
        <ResearchTabs aria-label="Research finding views">
          <ResearchTab
            type="button"
            aria-pressed={active === "journey"}
            onClick={() => setActive("journey")}
          >
            How people browsed
          </ResearchTab>
          <ResearchTab
            type="button"
            aria-pressed={active === "considerations"}
            onClick={() => setActive("considerations")}
          >
            What people needed
          </ResearchTab>
        </ResearchTabs>
        <ResearchPanel>
          <ResearchText>
            {active === "journey" ? (
              <>
                <span>
                  ""
                </span>
                <h3>Browsing was a comparison loop.</h3>
                <p>
                  People built a baseline, moved across providers, narrowed the
                  options, then checked again before they were ready to book.
                </p>
                <ComparisonRoute aria-label="Comparison loop">
                  <i>Find a baseline</i>
                  <i>Compare</i>
                  <i>Shortlist</i>
                  <i>Recheck</i>
                </ComparisonRoute>
              </>
            ) : (
              <>
                <span>""</span>
                <h3>Trust came from support across the journey.</h3>
                <p>
                  One reassurance message was not enough. People needed clear
                  information, flexibility, and a consistent sense of support.
                </p>
                <NeedGrid>
                  <i>Security</i>
                  <i>Easy comparison</i>
                  <i>Support for change</i>
                  <i>Consistency</i>
                </NeedGrid>
              </>
            )}
          </ResearchText>
          <ResearchImageButton
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Open the research visual"
          >
            <img
              src={activeImage}
              alt={
                active === "journey"
                  ? "User browsing journey showing comparison and planning before booking"
                  : "User considerations around price, time, and convenience"
              }
              loading="lazy"
            />
            <span>View full visual</span>
          </ResearchImageButton>
        </ResearchPanel>
      </ResearchShell>
      {open && (
        <Lightbox
          role="dialog"
          aria-modal="true"
          aria-label="Full research visual"
        >
          <LightboxClose type="button" onClick={() => setOpen(false)} autoFocus>
            Close
          </LightboxClose>
          <img src={activeImage} alt="Full research visual" />
        </Lightbox>
      )}
    </>
  );
};

const OutcomeSystem = () => {
  const [active, setActive] = useState<OutcomeKey>("prioritization");

  return (
    <OutcomeBoard>
      <OutcomeHeader>
        <h3>The audit became three practical outputs.</h3>
        <p>
          Each output helped a different group understand, discuss, or act on
          the work.
        </p>
      </OutcomeHeader>
      <OutcomeTabs aria-label="Project outputs">
        <OutcomeTab
          type="button"
          aria-pressed={active === "presentation"}
          onClick={() => setActive("presentation")}
        >
          <span>01</span>
          <strong>Presentation</strong>
          <small>Make the problem understandable</small>
        </OutcomeTab>
        <OutcomeTab
          type="button"
          aria-pressed={active === "workshop"}
          onClick={() => setActive("workshop")}
        >
          <span>02</span>
          <strong>Workshop</strong>
          <small>Build context with partners</small>
        </OutcomeTab>
        <OutcomeTab
          type="button"
          aria-pressed={active === "prioritization"}
          onClick={() => setActive("prioritization")}
        >
          <span>03</span>
          <strong>Prioritized direction</strong>
          <small>Turn findings into choices</small>
        </OutcomeTab>
      </OutcomeTabs>
      <OutcomePanel aria-live="polite">
        {active === "presentation" && (
          <>
            <OutcomeIntro>
              <span>Presentation</span>
              <h4>
                I helped shape the findings into a story people could follow.
              </h4>
              <p>
                We started with the user problem, explained the mental model,
                then connected it to the recommendations.
              </p>
            </OutcomeIntro>
            <StoryFlow>
              <StoryStep>
                <span>Why</span>
                <strong>The user problem and behavior</strong>
              </StoryStep>
              <StoryStep>
                <span>How</span>
                <strong>The journey and design principles</strong>
              </StoryStep>
              <StoryStep>
                <span>What</span>
                <strong>The recommended direction</strong>
              </StoryStep>
            </StoryFlow>
          </>
        )}
        {active === "workshop" && (
          <>
            <OutcomeIntro>
              <span>Workshop</span>
              <h4>The discussion connected research with business context.</h4>
              <p>
                I helped bring the right partners into the room and translated
                different inputs into shared questions.
              </p>
            </OutcomeIntro>
            <WorkshopVisual>
              <WorkshopCell>
                <span>Research</span>
                <strong>User mental model</strong>
              </WorkshopCell>
              <WorkshopCell>
                <span>Partners</span>
                <strong>Acquisition context</strong>
              </WorkshopCell>
              <WorkshopCell>
                <span>Product</span>
                <strong>Capability and constraints</strong>
              </WorkshopCell>
              <WorkshopCell result>
                <span>Shared output</span>
                <strong>Design direction</strong>
              </WorkshopCell>
            </WorkshopVisual>
          </>
        )}
        {active === "prioritization" && (
          <>
            <OutcomeIntro>
              <span>Prioritization with PM</span>
              <h4>
                I moved the recommendations into a working sheet and discussed
                the tradeoffs with PM.
              </h4>
              <p>
                We compared user value, strength of evidence, dependencies, and
                delivery effort before deciding where each direction should go.
              </p>
            </OutcomeIntro>
            <PrioritySheet>
              <table>
                <thead>
                  <tr>
                    <th>Direction</th>
                    <th>User value</th>
                    <th>Evidence</th>
                    <th>Dependency</th>
                    <th>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Make key information easier to compare</td>
                    <td>High</td>
                    <td>Strong</td>
                    <td>Manageable</td>
                    <td>
                      <span>First phase</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Strengthen confidence during browsing</td>
                    <td>High</td>
                    <td>Strong</td>
                    <td>Cross-team</td>
                    <td>
                      <span>Follow-up</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Connect acquisition context with the journey</td>
                    <td>Medium</td>
                    <td>Emerging</td>
                    <td>Another owner</td>
                    <td>
                      <span>Connected work</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Address broader platform opportunities</td>
                    <td>Broad</td>
                    <td>Needs discovery</td>
                    <td>High</td>
                    <td>
                      <span>Another project</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </PrioritySheet>
          </>
        )}
      </OutcomePanel>
    </OutcomeBoard>
  );
};

export default {
  title: "Reframing Mobile Web for Global Travelers",
  passwordProtected: true,
  timeline: "",
  context:
    "This was a cross-functional audit of Traveloka's flight mobile web experience during a shift from serving primarily Southeast Asian markets toward broader global expansion. Mobile web and desktop web were important acquisition surfaces for people arriving through search and paid channels. We used the audit to understand how unfamiliar users browsed, compared options, and built enough trust to continue. This case study focuses on the high-level concept and my role as Design PIC.",
  team: [
    "Design PICs: Freddy and Husein",
    "Interaction Designer: Pru",
    "Follow-up Visual Designer: Dira",
    "Partners across Product, Marketing, SEO, Research, and Engineering",
  ],
  thumbnail: "",
  contents: [
    {
      left: {
        title: "Mobile web was becoming a global front door",
        content: (
          <div>
            <p>
              Traveloka had built much of its Flight experience around
              established Southeast Asian markets, with product effort
              concentrated on the app and desktop web. Mobile web often mirrored
              the app and had not yet developed a clear role of its own.
            </p>
            <p>
              As the company prepared to reach broader global audiences, mobile
              web became more important. It was often the first place an
              unfamiliar user met Traveloka through search, advertising, or a
              shared link. The project was therefore not only an interface
              audit. It was part of defining how Traveloka could earn trust
              beyond its home markets.
            </p>
          </div>
        ),
      },
      right: {
        title: "Design question",
        content: (
          <Question>
            How might mobile web become a trusted first touchpoint for both
            established Southeast Asian markets and unfamiliar global users?
          </Question>
        ),
      },
      bottomContent: (
        <>
          <Hero>
            <HeroInner>
              <HeroCopy>
                <h2>A global front door is more than a screen.</h2>
                <p>
                  We used the audit to connect acquisition, user trust, product
                  strategy, information structure, delivery scope, and the
                  visible interface.
                </p>
              </HeroCopy>
              <IcebergVisual />
            </HeroInner>
          </Hero>
          <ExpansionContext />
        </>
      ),
    },
    {
      left: {
        title: "The bigger picture of my role",
        content: (
          <div>
            <p>
              Pru led the Interaction Design and research work. My role as PIC
              was to make sure she had the context, access, decisions, and
              stakeholder support needed to do the work well.
            </p>
            <p>
              I stayed close through checkpoints, research sessions, updates,
              and follow-ups. This kept the project moving without taking away
              the designer's ownership.
            </p>
          </div>
        ),
      },
      right: {
        title: "My PIC principle",
        content: (
          <Question>
            Maintain clarity around the work, so the designer can focus on the
            quality of the work.
          </Question>
        ),
      },
      bottomContent: <PicMaintainer />,
    },
    {
      left: {
        title: "Framing the design brief",
        content: (
          <div>
            <p>
              The project began as a broad request connected to acquisition and
              global expansion. Before asking the designer to execute, I
              conducted a Design team meeting and collected the context in the
              design brief and FigJam, then separated what we knew, what we
              assumed, what we needed to ask, and what decisions required
              partner input.
            </p>
            <p>
              I used this space as a working to-do list as well as a brief. It
              helped us divide responsibilities, track missing inputs, and keep
              the research connected to the original business context.
            </p>
          </div>
        ),
      },
      right: {
        title: "What I made visible",
        content: (
          <ul>
            <li>The role of mobile web in acquisition and global expansion</li>
            <li>
              The assumptions inherited from the app and established markets
            </li>
            <li>Questions that still needed evidence</li>
            <li>Potential actions, dependencies, and intersecting work</li>
            <li>Decisions we needed from product and marketing partners</li>
          </ul>
        ),
      },
      bottomContent: <BriefFramework />,
    },
    {
      left: {
        title: "The audit revealed more than interface issues",
        content: (
          <div>
            <p>
              We reviewed the journey from first visit to transaction and used
              the iceberg to organize what we found. Each layer moved the team
              further away from isolated screens and closer to the underlying
              user and product problem. This was especially important for global
              expansion because we could not rely on familiarity with Traveloka,
              its conventions, or the support behind the booking.
            </p>
          </div>
        ),
      },
      bottomContent: <LayerExplorer />,
    },
    {
      left: {
        title: "The main behavior was comparison",
        content: (
          <div>
            <p>
              Users used the browsing phase to find a baseline, narrow their
              choices, and compare timing, price, and convenience. This often
              happened across more than one platform.
            </p>
            <p>
              From here, trust did not mean one reassurance message. It meant
              helping users compare clearly, understand important details, and
              feel supported when the plan changed.
            </p>
          </div>
        ),
      },
      right: {
        title: "The high-level needs",
        content: (
          <ul>
            <li>A sense of security throughout the journey</li>
            <li>Enough information to compare without unnecessary effort</li>
            <li>Clear support for changes and uncertainties</li>
            <li>A consistent experience across devices and channels</li>
          </ul>
        ),
      },
      bottomContent: <ResearchEvidence />,
    },
    {
      left: {
        title: "The findings needed somewhere to go",
        content: (
          <div>
            <p>
              The audit was useful only if people could understand the findings,
              discuss them together, and decide what to do next. I helped turn
              the research into a presentation, a cross-functional workshop, and
              a prioritized direction.
            </p>
          </div>
        ),
      },
      right: {
        title: "Prioritizing with PM",
        content: (
          <p>
            I organized the recommendation directions in a working spreadsheet.
            Together with PM, we compared user value, evidence, dependencies,
            and effort, then separated first-phase work from follow-up ideas and
            larger projects.
          </p>
        ),
      },
      bottomContent: <OutcomeSystem />,
    },
    {
      left: {
        title: "Outcome",
        content: (
          <div>
            <p>
              The audit gave product and design a shared way to discuss mobile
              web beyond a list of interface fixes. It connected the user
              problem, global acquisition context, and delivery choices in one
              direction.
            </p>
            <p>
              The follow-up work could start from the audit instead of
              rebuilding the context from the beginning. One selected direction
              also moved into a later experiment and continued to be monitored.
            </p>
          </div>
        ),
      },
      right: {
        title: "What I am proud of",
        content: (
          <p>
            This was my first time leading a large, open-ended audit in the
            Transport team. I learned that PIC work is not only about checking
            progress. It is about creating the conditions for research,
            collaboration, decisions, and aftercare to stay connected.
          </p>
        ),
      },
      bottomContent: (
        <Impact>
          <ImpactLabel>
            <strong>3 connected outputs</strong>
          </ImpactLabel>
          <ImpactCopy>
            <h3>
              A presentation, a workshop, and a prioritized direction for a
              global-ready mobile web.
            </h3>
            <p>
              Together, they moved the audit from a broad initiative into a
              shared story, a cross-functional discussion, and a practical next
              step.
            </p>
          </ImpactCopy>
        </Impact>
      ),
    },
    {
      left: {
        title: "What I learned as Design PIC",
        content: (
          <div>
            <p>
              I prefer close collaboration, regular checkpoints, and being
              available when the designer needs context. Getting involved early
              made the work faster because decisions did not wait until the end.
            </p>
            <p>
              My biggest lesson was to maintain the thread between the brief,
              the research, the recommendation, and the next project. When that
              thread stays clear, a large project can move forward without
              losing the reason behind the work.
            </p>
          </div>
        ),
      },
    },
  ],
} as ProjectDetailProps;
